import {
  type ComponentProps,
  type ComponentType,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { debounce } from 'lodash';

import { useAccounts } from '../../../accounts/hooks/useAccounts';
import { extractAccountsByIds } from '../../../accounts/queryTransforms';
import { type Settings } from '../../../settings';
import { useRelatedIdAccountSearchEnabled } from '../../../settings/useRelatedIdAccountSearchEnabled';

import { Omnibar } from './index';
import { OmnibarTrigger } from './OmnibarTrigger';
import { useOmnibar } from './useOmnibar';
import { useOmnibarAccountSearch } from './useOmnibarAccountSearch';

type OmnibarProviderProps = {
  children: ReactNode;
  settings?: Settings;
  onSearchAccounts?: (query: string) => void;
  onClearAccountResults?: () => void;
};

type OmnibarContextValue = {
  query: string;
  onQueryChange: (query: string) => void;
  openOmnibar: () => void;
  closeOmnibar: () => void;
  isOpen: boolean;
  OmnibarTrigger: ComponentType<Omit<ComponentProps<typeof OmnibarTrigger>, 'onClick'>>;
};

const OmnibarContext = createContext<OmnibarContextValue | null>(null);

function ConnectedOmnibarTrigger(props: Omit<ComponentProps<typeof OmnibarTrigger>, 'onClick'>) {
  const { openOmnibar } = useOmnibarContext();
  return <OmnibarTrigger {...props} onClick={openOmnibar} />;
}

export function OmnibarProvider({ children, settings }: OmnibarProviderProps) {
  const relatedIdAccountSearchEnabled = useRelatedIdAccountSearchEnabled(settings);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const openOmnibar = useCallback(() => setIsOpen(true), []);
  const closeOmnibar = useCallback(() => {
    setIsOpen(false);
    setQuery('');
  }, []);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        openOmnibar();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openOmnibar]);

  const contextValue: OmnibarContextValue = {
    query,
    onQueryChange: setQuery,
    openOmnibar,
    closeOmnibar,
    isOpen,
    OmnibarTrigger: ConnectedOmnibarTrigger,
  };
  return (
    <OmnibarContext.Provider value={contextValue}>
      {children}
      {relatedIdAccountSearchEnabled ? <RelatedIdOmnibar settings={settings} /> : <LegacyOmnibar settings={settings} />}
    </OmnibarContext.Provider>
  );
}

function RelatedIdOmnibar({ settings }: { settings?: Settings }) {
  const { query } = useOmnibarContext();
  const search = useOmnibarAccountSearch(query);
  return <AccountOmnibar settings={settings} {...search} relatedIdAccountSearchEnabled />;
}

function LegacyOmnibar({ settings }: { settings?: Settings }) {
  const { query: initialQuery } = useOmnibarContext();
  const [query, setQuery] = useState(initialQuery);
  const { data, isLoading } = useAccounts({ q: query }, { enabled: query.length >= 2 });
  const debouncedSetQuery = useMemo(() => debounce(setQuery, 250), []);
  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);
  const accounts = useMemo(() => extractAccountsByIds(data), [data]);
  const search = (value: string) => {
    if (value.length >= 2) {
      debouncedSetQuery(value);
    } else {
      debouncedSetQuery.cancel();
      setQuery('');
    }
  };
  return <AccountOmnibar settings={settings} accounts={accounts} isLoading={isLoading} search={search} />;
}

type AccountOmnibarProps = Pick<ReturnType<typeof useOmnibarAccountSearch>, 'accounts' | 'isLoading' | 'search'> &
  Partial<Pick<ReturnType<typeof useOmnibarAccountSearch>, 'error' | 'reset' | 'retry'>> & {
    settings?: Settings;
    relatedIdAccountSearchEnabled?: boolean;
  };

function AccountOmnibar({
  settings,
  accounts,
  isLoading,
  error,
  search,
  reset,
  retry,
  relatedIdAccountSearchEnabled = false,
}: AccountOmnibarProps) {
  const { isOpen, closeOmnibar, query, onQueryChange } = useOmnibarContext();
  const {
    searchResults,
    isLoading: isOmnibarLoading,
    subscriptionFilter,
    closeOmnibar: clearQuery,
    handleSearch,
    handleSubscriptionFilterChange,
  } = useOmnibar({
    initialQuery: query,
    accounts,
    isAccountsLoading: isLoading,
    onSearchAccounts: search,
  });

  const handleClose = () => {
    reset?.();
    clearQuery();
    closeOmnibar();
  };

  return (
    <Omnibar
      isOpen={isOpen}
      initialQuery={query}
      onClose={handleClose}
      onSearch={(value) => {
        onQueryChange(value);
        handleSearch(value);
      }}
      searchResults={searchResults}
      isLoading={isOmnibarLoading}
      error={error}
      onRetry={retry}
      relatedIdAccountSearchEnabled={relatedIdAccountSearchEnabled}
      subscriptionFilter={subscriptionFilter}
      onSubscriptionFilterChange={handleSubscriptionFilterChange}
      enableFilters={settings?.enableSubscriptionStateFiltering}
      enablePlanTypeInSearch={settings?.enablePlanTypeInSearch}
    />
  );
}

// Hook to use the omnibar context
export const useOmnibarContext = (): OmnibarContextValue => {
  const context = useContext(OmnibarContext);
  if (!context) {
    throw new Error('useOmnibarContext must be used within an OmnibarProvider');
  }
  return context;
};

export default OmnibarProvider;
